// Условия экрана «Оповещения» в адресе страницы (002: contracts/ui.md, «/ops/alerts»; FR-045):
// «Действуют» или «История», вид сбоя, период (только в истории) и порядок по времени начала.
// Период — даты в поясе установки; в запрос уходят моменты начала и конца дня, как в «Копиях».
import { alertKinds, alertSortFields } from '@kp/contracts';
import type { AlertKind, AlertsQuery, SortValue } from '@kp/contracts';
import { dayEnd, dayStart } from '../../app/format.ts';

/** «Действуют» — только действующие; «История» — все за срок хранения. */
export type AlertsView = 'active' | 'history';

export type AlertSort = SortValue<(typeof alertSortFields)[number]>;

export const DEFAULT_ALERTS_SORT: AlertSort = '-startedAt';

export interface AlertsSearch {
  /** Нет — «Действуют». */
  view?: 'history';
  kind?: AlertKind;
  /** Первый день периода: 2026-10-14; только в истории. */
  from?: string;
  /** Последний день периода включительно. */
  to?: string;
  sort?: AlertSort;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const sortValues: readonly string[] = alertSortFields.flatMap((field) => [field, `-${field}`]);

/** Для адреса страницы: validateSearch маршрута /ops/alerts. */
export function parseAlertsSearch(search: Record<string, unknown>): AlertsSearch {
  const result: AlertsSearch = {};
  if (search.view === 'history') result.view = 'history';
  if (typeof search.kind === 'string') {
    const kind = alertKinds.find((known) => known === search.kind);
    if (kind) result.kind = kind;
  }
  if (typeof search.from === 'string' && DATE.test(search.from)) result.from = search.from;
  if (typeof search.to === 'string' && DATE.test(search.to)) result.to = search.to;
  if (typeof search.sort === 'string' && sortValues.includes(search.sort)) {
    result.sort = search.sort as AlertSort;
  }
  return result;
}

/** Переключатель: период остаётся только у истории, вид и порядок — у обоих. */
export function withView(search: AlertsSearch, view: AlertsView): AlertsSearch {
  if (view === 'history') return { ...search, view: 'history' };
  return { ...search, view: undefined, from: undefined, to: undefined };
}

/** Условия сужают список: «Ничего не найдено» вместо «Сбоев нет». */
export function hasAlertsFilters(search: AlertsSearch): boolean {
  return Boolean(search.kind || (search.view === 'history' && (search.from || search.to)));
}

/** Условия запроса к серверу. */
export function alertsFilters(search: AlertsSearch, timeZone: string): AlertsQuery {
  const history = search.view === 'history';
  return {
    state: history ? undefined : 'firing',
    kind: search.kind,
    from: history && search.from ? dayStart(search.from, timeZone) : undefined,
    to: history && search.to ? dayEnd(search.to, timeZone) : undefined,
    sort: search.sort ?? DEFAULT_ALERTS_SORT,
  };
}
